import { Link } from "@/lib/router-compat";
import { useParams } from "@/lib/router-compat";
import { ArrowRight, Phone } from "lucide-react";
import Layout from "@/components/Layout";
import PageSEO from "@/components/PageSEO";
import PageHero from "@/components/PageHero";
import FinalContactSection from "@/components/FinalContactSection";
import PageJumpNav from "@/components/PageJumpNav";
import RailContactCard from "@/components/RailContactCard";
import TestimonialRow from "@/components/TestimonialRow";
import { COMPANY, faqSchema, breadcrumbSchema, serviceSchema } from "@/lib/seo";
import { guideFaqs } from "@/data/guide-faqs";
import { guides } from "@/data/guides";
import { resolveRedirect } from "@/lib/redirect-map";


const GuidePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const guide = guides.find(g => g.slug === slug);

  if (!guide) {
    return <Layout><div className="container section-padding text-center"><h1 className="font-heading text-3xl font-bold">Guide Not Found</h1><Link to="/" className="text-primary mt-4 inline-block">Return Home</Link></div></Layout>;
  }

  return (
    <Layout>
      <PageSEO
        title={guide.metaTitle}
        description={guide.metaDescription}
        canonical={`/guide/${guide.slug}`}
        schemas={[
          breadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Guides", url: "/blog" },
            { name: guide.title, url: `/guide/${guide.slug}` },
          ]),
          ...(guideFaqs[guide.slug] ? [faqSchema(guideFaqs[guide.slug]!)].filter(Boolean) : []),
          serviceSchema({ name: guide.title, description: guide.metaDescription, url: `/guide/${guide.slug}`, category: "Guide" }),
          // ItemList JSON-LD per pricing section — improves answer-engine extraction
          ...guide.sections
            .filter(s => s.items && s.items.length > 0)
            .map(section => ({
              "@context": "https://schema.org",
              "@type": "ItemList",
              name: `${section.heading} — ${guide.title}`,
              description: section.content,
              url: `/guide/${guide.slug}#${section.heading.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`,
              numberOfItems: section.items!.length,
              itemListOrder: "https://schema.org/ItemListOrderAscending",
              itemListElement: section.items!.map((item, idx) => ({
                "@type": "ListItem",
                position: idx + 1,
                name: item,
              })),
            })),
        ]}

      />
      <PageHero title={guide.title} eyebrow="GUIDE" lede={guide.intro} breadcrumbs={[{ label: "Resources" }, { label: guide.title }]} />

      <section className="section-padding">
        <div className="container grid gap-12 lg:grid-cols-[minmax(0,720px)_280px] lg:justify-between">
          <div className="min-w-0 font-body text-[18px] leading-[1.75]">
          <p className="mb-10 text-muted-foreground">{guide.intro}</p>

          {guide.sections.map((section, i) => (
              <section key={i} className="mb-12 border-t border-border pt-8">
                <h2 id={section.heading.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")} className="font-heading text-[28px] font-bold leading-tight">{section.heading}</h2>
                <p className="text-muted-foreground font-body mb-4 leading-relaxed">{section.content}</p>
                {section.items && (
                  <ol className="space-y-4">
                    {section.items.map((item, j) => (
                      <li key={j} className="grid grid-cols-[44px_1fr] gap-4 border-t border-border pt-4 first:border-0"><span className="font-condensed text-[40px] font-bold leading-none text-primary">{j + 1}</span><span className="font-body text-base leading-relaxed text-muted-foreground">{item}</span></li>
                    ))}
                  </ol>
                )}
              </section>
          ))}

          {/* Louisville-focused FAQ — emitted as FAQPage schema above */}
          {guideFaqs[guide.slug] && (
              <div className="mt-12 pt-8 border-t border-border">
                <h2 id="frequently-asked-questions-louisville-ky" className="font-heading text-[28px] font-bold mb-6">Frequently Asked Questions — Louisville, KY</h2>
                <div className="space-y-4">
                  {guideFaqs[guide.slug]!.map((faq, i) => (
                    <div key={i} className="border-t border-border py-5 last:border-b"><h3 className="font-heading text-[18px] font-bold">{faq.question}</h3><p className="mt-2 text-muted-foreground font-body leading-relaxed">{faq.answer}</p></div>
                  ))}
                </div>
              </div>
          )}

          {/* Related guides — rotates through siblings so every guide receives 3+ incoming internal links from peers */}
          {(() => {
            // Consolidated guides now 301 elsewhere — never link internally to a redirect hop.
            const linkable = guides.filter(g => !resolveRedirect(`/guide/${g.slug}`));
            const idx = linkable.findIndex(g => g.slug === guide.slug);
            const others = linkable.filter((_, i) => i !== idx);
            const related = [1, 2, 3].map(offset => others[(idx + offset + others.length) % others.length]).filter((g): g is NonNullable<typeof g> => Boolean(g));

            return (
                <div className="mt-12 pt-8 border-t border-border">
                  <h2 id="related-guides" className="font-heading text-2xl font-bold mb-6">Related Guides</h2>
                  <div className="grid sm:grid-cols-3 gap-4">
                    {related.map(g => (
                      <Link
                        key={g.slug}
                        to={`/guide/${g.slug}`}
                        className="group border-t border-border py-4 hover:text-primary"
                      >
                        <div className="flex-1">
                          <span className="font-heading font-semibold text-sm block leading-tight">{g.title}</span>
                          <span className="text-xs text-primary font-semibold inline-flex items-center gap-1 mt-1">Read Guide <ArrowRight className="w-3 h-3" /></span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
            );
          })()}

            <div className="border-l-[3px] border-primary bg-charcoal p-8 text-charcoal-foreground mt-12">
              <h3 className="font-heading text-2xl font-bold mb-4">Ready to Get Started?</h3>
              <p className="text-muted-foreground font-body mb-6">Call us at {COMPANY.phone} or request a free estimate online.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/request-estimate" className="btn-primary px-8 py-4 text-lg">
                  {guide.cta} <ArrowRight className="w-5 h-5" />
                </Link>
                <a href={`tel:${COMPANY.phoneTel}`} className="btn-outline-light px-8 py-4 text-lg bg-charcoal text-charcoal-foreground border-charcoal-foreground/20">
                  <Phone className="w-5 h-5" /> {COMPANY.phone}
                </a>
              </div>
            </div>
          </div>
          <aside className="hidden lg:block"><div className="sticky top-28 space-y-6"><PageJumpNav headings={guide.sections.map(section => ({ id: section.heading.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, ""), text: section.heading }))} /><RailContactCard dataCta="guide-rail" /></div></aside>
        </div>
      </section>
      <TestimonialRow />
      <FinalContactSection source="guide_form" />
    </Layout>
  );
};

export default GuidePage;
